import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import {
  containsHyphenInQuote,
  formatDollarFractionString,
  formatDollarFractionZValueForSmallFractionDisplay,
  returnFractionString,
  ruleOfEights,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import tradeDataStore from "@/store/tradeDataStore";
import { formatLevelValueWithNotation } from "./levelFormatter";
import { getNotation, getOrderType, getReferencePrice } from "./levelHelpers";

/**
 * LevelCellRenderer
 *
 * Displays the order level value - "Market" for Market orders,
 * or the referencePrice value with notation suffix for Limit/Stop orders.
 * Shows nothing when no value is entered.
 *
 * Works in conjunction with levelValueFormatter for copy/paste support (without notation).
 */
const LevelCellRenderer = (params: any) => {
  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);

  // Subscribe to Zustand store for reactive updates, fallback to fullTradeData for historical trades
  const currentTradeData = tradeDataStore((state) => state.tradeData);
  const thisTrade =
    currentTradeData.find((trade) => trade.esid == fullTradeData.esid) ??
    fullTradeData;

  const orderType = getOrderType(params, thisTrade);

  if (orderType === "Market") {
    return (
      <div
        data-testid="LevelCellContainer"
        id="LevelCellContainer"
        className="justify-center text-center"
        style={{ width: "100%" }}
      >
        Market
      </div>
    );
  }

  const notation = getNotation(params, thisTrade);
  const referencePrice = getReferencePrice(params, thisTrade);

  // Handle $fr notation: convert Z-value digit to superscript fraction (e.g., "116-023" → "116-02 ³⁄₈ $fr")
  if (
    notation === "$fr" &&
    referencePrice &&
    typeof referencePrice === "string" &&
    containsHyphenInQuote(referencePrice)
  ) {
    const fractionStr = formatDollarFractionString(referencePrice);
    const valueAfterHyphen = returnFractionString(referencePrice, false);

    if (valueAfterHyphen.length === 3) {
      const quoteDisplayValue = fractionStr.slice(0, -1);
      const zValueDecimal = formatDollarFractionZValueForSmallFractionDisplay(referencePrice, ruleOfEights);
      return (
        <div
          data-testid="LevelCellContainer"
          id="LevelCellContainer"
          className="justify-center text-center"
          style={{ width: "100%" }}
        >
          {quoteDisplayValue}{zValueDecimal}
        </div>
      );
    }

    return (
      <div
        data-testid="LevelCellContainer"
        id="LevelCellContainer"
        className="justify-center text-center"
        style={{ width: "100%" }}
      >
        {fractionStr}
      </div>
    );
  }

  // For all other notations, use the existing string formatter
  const formattedValue = formatLevelValueWithNotation(params, thisTrade);

  return (
    <div
      data-testid="LevelCellContainer"
      id="LevelCellContainer"
      className="justify-center text-center"
      style={{ width: "100%" }}
    >
      {formattedValue}
    </div>
  );
};

export default LevelCellRenderer;
